import { supabase } from '../lib/supabase';

const AIMLAPI_KEY = import.meta.env?.VITE_AIMLAPI_KEY;
const AIMLAPI_BASE_URL = 'https://api.aimlapi.com/v1';

/**
 * Service for analyzing sales conversations against frameworks
 */
export const conversationAnalyzerService = {
  /**
   * Analyze a conversation transcript or email against sales frameworks
   * @param {Object} params - Analysis parameters
   * @returns {Promise<Object>} Analysis results with scores and feedback
   */
  async analyzeConversation({ conversationType, originalContent, industry, tenantId = null }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build analysis prompt
      const systemPrompt = `You are Lumo, a sales coaching AI that analyzes conversations for technique effectiveness.

Your job is to:
1. Identify which sales techniques are present (SPIN questions, P.I.N.E., objection handling, closing techniques)
2. Rate effectiveness in these areas (0-100):
   - Discovery (asking good questions, understanding buyer)
   - Objection Handling (addressing concerns effectively)
   - Closing (moving toward commitment)
3. Identify gaps (what techniques were missing)
4. Suggest specific improvements

Framework Reference:
- SPIN: Situation, Problem, Implication, Need-Payoff questions
- P.I.N.E.: Problem identification, Implication exploration, Need confirmation, Execution planning
- Objection Handling: Validate, understand root cause, reframe with evidence
- Closing: Assumptive closes, summary closes, urgency closes

Return JSON with this structure:
{
  "framework_analysis": {
    "spin_questions_found": 3,
    "pine_framework_used": true,
    "objection_handles": 1,
    "closing_technique": "assumptive",
    "techniques_identified": ["problem_questions", "implication_questions", "social_proof"]
  },
  "discovery_score": 75,
  "objection_handling_score": 60,
  "closing_score": 70,
  "overall_score": 68,
  "gap_analysis": {
    "missing_techniques": ["need_payoff_questions", "multiple_objection_handles"],
    "improvement_areas": ["deeper implication exploration", "stronger social proof"]
  },
  "improvement_suggestions": [
    "Add more implication questions to uncover buyer urgency",
    "When handling price objection, use ROI reframe instead of just defending price",
    "Try assumptive close language: 'When we get started...' instead of 'If you decide to...'"
  ]
}`;

      const userPrompt = `Analyze this ${conversationType}:

${originalContent}

Industry: ${industry || 'General'}`;

      // Call AIMLAPI for analysis
      const response = await fetch(`${AIMLAPI_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AIMLAPI_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.7,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      const analysisText = result?.choices?.[0]?.message?.content;

      // Parse JSON response
      let analysis;
      try {
        const jsonText = analysisText?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
        analysis = JSON.parse(jsonText);
      } catch (e) {
        throw new Error('Failed to parse analysis results');
      }

      // Save to database
      const { data: savedAnalysis, error: saveError } = await supabase
        .from('conversation_analyses')
        .insert({
          user_id: user.id,
          conversation_type: conversationType,
          original_content: originalContent,
          framework_analysis: analysis.framework_analysis,
          technique_breakdown: analysis.technique_breakdown,
          gap_analysis: analysis.gap_analysis,
          improvement_suggestions: analysis.improvement_suggestions,
          discovery_score: analysis.discovery_score,
          objection_handling_score: analysis.objection_handling_score,
          closing_score: analysis.closing_score,
          overall_score: analysis.overall_score,
          industry: industry,
          ai_model: 'gpt-4o-mini',
          tenant_id: tenantId || null
        })
        .select()
        .single();

      if (saveError) {
        console.error('Error saving analysis:', saveError);
      }

      return {
        ...analysis,
        analysisId: savedAnalysis?.id,
        savedAt: savedAnalysis?.created_at
      };
    } catch (error) {
      console.error('Error analyzing conversation:', error);
      throw error;
    }
  },

  /**
   * Get all analyses for current user
   * @returns {Promise<Array>} List of analyses
   */
  async getUserAnalyses() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('conversation_analyses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching analyses:', error);
      throw error;
    }
  },

  /**
   * Get a specific analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Object>} Analysis details
   */
  async getAnalysis(analysisId, tenantId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('conversation_analyses')
        ?.select('*')
        ?.eq('id', analysisId)
        ?.eq('user_id', user.id);

      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching analysis:', error);
      throw error;
    }
  }
};

/**
 * Service for AI role-play practice sessions
 */
export const roleplayService = {
  /**
   * Phone call sequence stages - 6-step framework
   */
  PHONE_SEQUENCE_STAGES: [
    {
      number: 1,
      name: 'permission',
      scriptGoal: 'founding_opener',
      title: 'Permission & Rapport',
      instruction: 'Start with the Foundational Opener - ask permission before launching into your pitch',
      coachingFocus: 'Does your opening feel natural? Did you ask permission?'
    },
    {
      number: 2,
      name: 'purpose',
      scriptGoal: 'purpose_statement',
      title: 'Purpose Framing',
      instruction: 'State your reason for calling neutrally - let them self-identify with the problem',
      coachingFocus: 'Did you avoid pitching? Did you state the reason clearly?'
    },
    {
      number: 3,
      name: 'discovery',
      scriptGoal: 'discovery',
      title: 'Discovery Question',
      instruction: 'Ask an open-ended discovery question about their current situation',
      coachingFocus: 'Did you ask open-ended? Did you let silence work?'
    },
    {
      number: 4,
      name: 'clarify',
      scriptGoal: 'clarify_objection',
      title: 'Clarify Concerns',
      instruction: 'When an objection appears, clarify it before responding',
      coachingFocus: 'Did you ask clarifying questions? Did you assume or ask?'
    },
    {
      number: 5,
      name: 'diffuse',
      scriptGoal: 'diffuse_objection',
      title: 'Diffuse Objections',
      instruction: 'Use hypothetical framing to remove pressure and invite self-persuasion',
      coachingFocus: 'Did you use conditional language? Did they feel pressured?'
    },
    {
      number: 6,
      name: 'close',
      scriptGoal: 'commitment_close',
      title: 'Self-Persuasion Close',
      instruction: 'Ask for their opinion on the solution, then ask why - the second question closes the deal',
      coachingFocus: 'Did you ask for their opinion first? Did you get them to persuade themselves?'
    }
  ],

  /**
   * Get current phone sequence stage
   */
  getPhoneSequenceStage(stageIndex) {
    return this.PHONE_SEQUENCE_STAGES[Math.min(stageIndex || 0, this.PHONE_SEQUENCE_STAGES.length - 1)];
  },

  /**
   * Create a new role-play session
   * @param {Object} params - Session parameters
   * @returns {Promise<Object>} New session
   */
  async createSession({ practiceType, difficultyLevel, industry, salesStage, scenarioBriefing, tenantId = null }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Initialize phone sequence data if phone_sequence mode
      let phoneSequenceData = null;
      if (practiceType === 'phone_sequence') {
        phoneSequenceData = {
          currentStage: 0,
          completedStages: [],
          stageScores: {}
        };
      }

      const { data: session, error } = await supabase
        .from('roleplay_sessions')
        .insert({
          user_id: user.id,
          difficulty_level: difficultyLevel || 'beginner',
          industry,
          sales_stage: salesStage,
          scenario_briefing: scenarioBriefing,
          practice_type: practiceType || 'general',
          phone_sequence_data: phoneSequenceData,
          status: 'in_progress',
          ai_model: 'gpt-4o-mini',
          tenant_id: tenantId || null
        })
        .select()
        .single();

      if (error) throw error;

      return session;
    } catch (error) {
      console.error('Error creating role-play session:', error);
      throw error;
    }
  },

  /**
   * Generate AI prospect response
   * @param {Object} params - Response parameters
   * @returns {Promise<Object>} AI response and feedback
   */
  async generateProspectResponse({ sessionId, userMessage, conversationHistory, difficultyLevel, industry, salesStage, practiceType, phoneSequenceData }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build context for AI prospect
      const difficultyPrompts = {
        beginner: 'Be a cooperative prospect who is generally open but has some natural hesitations.',
        intermediate: 'Be a thoughtful prospect who asks clarifying questions and has some objections.',
        advanced: 'Be a skeptical prospect who challenges assumptions and requires strong evidence.',
        expert: 'Be a highly sophisticated buyer who tests selling techniques and won\'t accept weak reasoning.'
      };

      // Get phone sequence stage if applicable
      let phoneStageContext = '';
      if (practiceType === 'phone_sequence' && phoneSequenceData) {
        const currentStage = this.getPhoneSequenceStage(phoneSequenceData?.currentStage || 0);
        phoneStageContext = `\nPHONE CALL STAGE ${currentStage.number}/6: ${currentStage.title}
Focus: ${currentStage.coachingFocus}
Goal: User should be using the "${currentStage.scriptGoal}" script technique.`;
      }

      const systemPrompt = `You are a sales prospect in a role-play exercise.

Your role:
- You are a ${industry} buyer considering a solution
- Current sales stage: ${salesStage}
- Difficulty level: ${difficultyLevel}
- ${difficultyPrompts[difficultyLevel]}${phoneStageContext}

Instructions:
1. Respond naturally as a prospect would
2. Ask clarifying questions
3. Raise realistic objections for your difficulty level
4. Give feedback on the salesperson's techniques (end-of-message)
5. Keep responses concise (2-3 sentences for prospect dialogue)

After the conversation, provide brief JSON feedback:
{
  "prospect_response": "Your response to the salesperson",
  "technique_detected": "What technique the rep used (e.g., 'discovery_question', 'objection_reframe')",
  "effectiveness_rating": 7,
  "feedback": "Specific feedback on their approach"
}`;

      const conversationContext = conversationHistory
        .map(msg => `${msg.role === 'user' ? 'Salesperson' : 'Prospect'}: ${msg.content}`)
        .join('\n');

      const userPrompt = `${conversationContext}

Salesperson: ${userMessage}

Respond as the prospect and provide feedback in JSON format.`;

      // Call AIMLAPI
      const response = await fetch(`${AIMLAPI_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AIMLAPI_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.8,
          max_tokens: 500
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      const responseText = result?.choices?.[0]?.message?.content;

      // Parse JSON response - extract JSON from text
      let prospectData;
      try {
        // Look for JSON block first (between ``` markers)
        let jsonMatch = responseText?.match(/```json\s*([\s\S]*?)```/);
        let jsonText = jsonMatch?.[1] || responseText?.match(/```\s*([\s\S]*?)```/)?.[1];
        
        // If no code block, try to extract JSON object directly
        if (!jsonText) {
          jsonMatch = responseText?.match(/\{[\s\S]*\}/);
          jsonText = jsonMatch?.[0];
        }
        
        if (!jsonText) {
          throw new Error('No JSON found in response');
        }
        
        prospectData = JSON.parse(jsonText.trim());
      } catch (e) {
        // Fallback: extract prospect response from text before JSON
        const prospectMatch = responseText?.match(/([^{]*)/)?.[1]?.trim();
        prospectData = {
          prospect_response: prospectMatch || responseText,
          technique_detected: 'unknown',
          effectiveness_rating: 5,
          feedback: 'Response processed'
        };
      }

      return prospectData;
    } catch (error) {
      console.error('Error generating prospect response:', error);
      throw error;
    }
  },

  /**
   * Update session with new message
   * @param {Object} params - Update parameters
   * @returns {Promise<Object>} Updated session
   */
  async updateSessionMessage({ sessionId, userMessage, prospectResponse, techniqueUsed, effectivenessRating, phoneSequenceData, tenantId = null }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current session
      let fetchQuery = supabase
        ?.from('roleplay_sessions')
        ?.select('*')
        ?.eq('id', sessionId)
        ?.eq('user_id', user.id);

      if (tenantId) {
        fetchQuery = fetchQuery?.eq('tenant_id', tenantId);
      }

      const { data: session, error: fetchError } = await fetchQuery?.single();

      if (fetchError) throw fetchError;

      // Update conversation history
      const updatedHistory = [
        ...(session.conversation_history || []),
        { role: 'user', content: userMessage },
        { role: 'assistant', content: prospectResponse }
      ];

      // Track technique usage
      const updatedTechniques = [...(session.techniques_used || [])];
      if (techniqueUsed && !updatedTechniques.includes(techniqueUsed)) {
        updatedTechniques.push(techniqueUsed);
      }

      // Build update payload
      const updatePayload = {
        conversation_history: updatedHistory,
        techniques_used: updatedTechniques,
        updated_at: new Date().toISOString()
      };

      // Add phone sequence data if provided
      if (phoneSequenceData) {
        updatePayload.phone_sequence_data = phoneSequenceData;
      }

      // Update session
      let updateQuery = supabase
        ?.from('roleplay_sessions')
        ?.update(updatePayload)
        ?.eq('id', sessionId)
        ?.eq('user_id', user.id);

      if (tenantId) {
        updateQuery = updateQuery?.eq('tenant_id', tenantId);
      }

      const { data: updated, error: updateError } = await updateQuery?.select()?.single();

      if (updateError) throw updateError;

      // Save feedback for this message
      if (techniqueUsed || effectivenessRating) {
        await supabase
          .from('roleplay_feedback')
          .insert({
            session_id: sessionId,
            user_message_index: updatedHistory.length - 2,
            technique_identified: techniqueUsed,
            effectiveness_rating: effectivenessRating,
            improvement_suggestions: []
          });
      }

      return updated;
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  },

  /**
   * Complete role-play session with final scores
   * @param {Object} params - Completion parameters
   * @returns {Promise<Object>} Completed session with scores
   */
  async completeSession({ sessionId, discoveryScore, objectionScore, closingScore, overallScore, tenantId = null }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let completeQuery = supabase
        ?.from('roleplay_sessions')
        ?.update({
          status: 'completed',
          discovery_score: discoveryScore,
          objection_handling_score: objectionScore,
          closing_score: closingScore,
          overall_score: overallScore,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        ?.eq('id', sessionId)
        ?.eq('user_id', user?.id);

      if (tenantId) {
        completeQuery = completeQuery?.eq('tenant_id', tenantId);
      }

      const { data: completed, error } = await completeQuery?.select()?.single();

      if (error) throw error;

      return completed;
    } catch (error) {
      console.error('Error completing session:', error);
      throw error;
    }
  },

  /**
   * Get all sessions for user
   * @returns {Promise<Array>} List of sessions
   */
  async getUserSessions() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('roleplay_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  },

  /**
   * Get a specific session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Session details
   */
  async getSession(sessionId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('roleplay_sessions')
        .select('*')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching session:', error);
      throw error;
    }
  }
};
